import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import * as api from './reminders_api';
import type { AsyncStatus } from './personal_slice';

export interface RemindersEntry<T> {
  data: T | null;
  status: AsyncStatus;
  error: string | null;
  /** The request whose result this entry is waiting for; a result from any
   *  other (one started before a logout, or for another group) is dropped. */
  requestId?: string;
}

/** An entry for one group. Its data is for `groupId` only: a reader viewing
 *  another group treats it as not loaded and asks again. */
export interface GroupEntry<T> extends RemindersEntry<T> {
  groupId: number | null;
}

/** The audience depends on the reminder's type as well as the group. */
export interface AudienceEntry extends GroupEntry<api.ReminderAudience> {
  reminderType: api.ReminderType | null;
}

// Never persisted, and reset with the rest of the store on logout.
export interface RemindersState {
  templates: RemindersEntry<api.ReminderTemplate[]>;
  audience: AudienceEntry;
  history: GroupEntry<api.Reminder[]>;
  myNotices: RemindersEntry<api.MyReminder[]>;
  send: { status: AsyncStatus; error: string | null };
  cancel: { id: number | null; error: string | null };
}

const idle = <T>(): RemindersEntry<T> => ({ data: null, status: 'idle', error: null });
const idleForGroup = <T>(): GroupEntry<T> => ({ ...idle<T>(), groupId: null });

const initialState: RemindersState = {
  templates: idle(),
  audience: { ...idleForGroup<api.ReminderAudience>(), reminderType: null },
  history: idleForGroup(),
  myNotices: idle(),
  send: { status: 'idle', error: null },
  cancel: { id: null, error: null },
};

type ThunkConfig = { state: RootState; rejectValue: string };

export const fetchReminderTemplates = createAsyncThunk<api.ReminderTemplate[], void, ThunkConfig>(
  'reminders/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      return await api.getReminderTemplates();
    } catch (error) {
      return rejectWithValue(api.reminderErrorMessage(error, 'Failed to load reminder templates'));
    }
  }
);

export const fetchReminderAudience = createAsyncThunk<
  api.ReminderAudience, { groupId: number; reminderType: api.ReminderType }, ThunkConfig
>(
  'reminders/fetchAudience',
  async ({ groupId, reminderType }, { rejectWithValue }) => {
    try {
      return await api.getReminderAudience(groupId, reminderType);
    } catch (error) {
      return rejectWithValue(api.reminderErrorMessage(error, 'Failed to load who the reminder would reach'));
    }
  }
);

export const fetchReminderHistory = createAsyncThunk<api.Reminder[], number, ThunkConfig>(
  'reminders/fetchHistory',
  async (groupId, { rejectWithValue }) => {
    try {
      return await api.getReminders(groupId);
    } catch (error) {
      return rejectWithValue(api.reminderErrorMessage(error, 'Failed to load past reminders'));
    }
  }
);

export const sendReminder = createAsyncThunk<api.Reminder, api.NewReminder, ThunkConfig>(
  'reminders/send',
  async (reminder, { rejectWithValue }) => {
    try {
      return await api.createReminder(reminder);
    } catch (error) {
      return rejectWithValue(api.reminderErrorMessage(error, 'The reminder could not be sent. Please try again.'));
    }
  },
  { condition: (_, { getState }) => getState().reminders.send.status !== 'loading' }
);

export const cancelReminder = createAsyncThunk<api.Reminder, number, ThunkConfig>(
  'reminders/cancel',
  async (id, { rejectWithValue }) => {
    try {
      return await api.cancelReminder(id);
    } catch (error) {
      return rejectWithValue(api.reminderErrorMessage(error, 'The reminder could not be cancelled.'));
    }
  },
  { condition: (_, { getState }) => getState().reminders.cancel.id === null }
);

export const fetchMyReminders = createAsyncThunk<api.MyReminder[], void, ThunkConfig>(
  'reminders/fetchMine',
  async (_, { rejectWithValue }) => {
    try {
      return await api.getMyReminders();
    } catch (error) {
      return rejectWithValue(api.reminderErrorMessage(error, 'Failed to load reminders'));
    }
  },
  { condition: (_, { getState }) => getState().reminders.myNotices.status !== 'loading' }
);

export const dismissMyReminder = createAsyncThunk<unknown, number, ThunkConfig>(
  'reminders/dismissMine',
  async (id) => api.dismissMyReminder(id)
);

type Settable<T> = RemindersEntry<T> | GroupEntry<T>;
interface ThunkMeta { meta: { requestId: string; arg?: unknown } }

const start = <T>(entry: Settable<T>, action: ThunkMeta) => {
  entry.status = 'loading';
  entry.error = null;
  entry.requestId = action.meta.requestId;
};
const succeed = <T>(entry: Settable<T>, action: ThunkMeta & { payload: T }) => {
  if (entry.requestId !== action.meta.requestId) return;
  entry.status = 'succeeded';
  entry.data = action.payload;
  delete entry.requestId;
};
const fail = <T>(entry: Settable<T>, action: ThunkMeta & { payload?: string; error: { message?: string } }) => {
  if (entry.requestId !== action.meta.requestId) return;
  entry.status = 'failed';
  entry.error = action.payload ?? action.error.message ?? null;
  delete entry.requestId;
};
/** A group entry asked for another group drops what it held for the last one. */
const startForGroup = <T>(entry: GroupEntry<T>, action: ThunkMeta & { meta: { arg: number } }) => {
  if (entry.groupId !== action.meta.arg) entry.data = null;
  entry.groupId = action.meta.arg;
  start(entry, action);
};

const remindersSlice = createSlice({
  name: 'reminders',
  initialState,
  reducers: {
    clearSendStatus: (state) => {
      state.send = { status: 'idle', error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReminderTemplates.pending, (state, action) => start(state.templates, action))
      .addCase(fetchReminderTemplates.fulfilled, (state, action) => succeed(state.templates, action))
      .addCase(fetchReminderTemplates.rejected, (state, action) => fail(state.templates, action))
      .addCase(fetchReminderAudience.pending, (state, action) => {
        const { groupId, reminderType } = action.meta.arg;
        if (state.audience.groupId !== groupId || state.audience.reminderType !== reminderType) state.audience.data = null;
        state.audience.groupId = groupId;
        state.audience.reminderType = reminderType;
        start(state.audience, action);
      })
      .addCase(fetchReminderAudience.fulfilled, (state, action) => succeed(state.audience, action))
      .addCase(fetchReminderAudience.rejected, (state, action) => fail(state.audience, action))
      .addCase(fetchReminderHistory.pending, (state, action) => startForGroup(state.history, action))
      .addCase(fetchReminderHistory.fulfilled, (state, action) => succeed(state.history, action))
      .addCase(fetchReminderHistory.rejected, (state, action) => fail(state.history, action))
      .addCase(fetchMyReminders.pending, (state, action) => start(state.myNotices, action))
      .addCase(fetchMyReminders.fulfilled, (state, action) => succeed(state.myNotices, action))
      .addCase(fetchMyReminders.rejected, (state, action) => fail(state.myNotices, action))
      .addCase(sendReminder.pending, (state) => {
        state.send = { status: 'loading', error: null };
      })
      .addCase(sendReminder.fulfilled, (state, action) => {
        state.send = { status: 'succeeded', error: null };
        if (state.history.groupId === action.payload.group_id && state.history.data) {
          state.history.data = [action.payload, ...state.history.data];
        }
      })
      .addCase(sendReminder.rejected, (state, action) => {
        state.send = { status: 'failed', error: action.payload ?? action.error.message ?? null };
      })
      .addCase(cancelReminder.pending, (state, action) => {
        state.cancel = { id: action.meta.arg, error: null };
      })
      .addCase(cancelReminder.fulfilled, (state, action) => {
        state.cancel = { id: null, error: null };
        if (state.history.data) {
          state.history.data = state.history.data.map((r) => (r.id === action.payload.id ? action.payload : r));
        }
      })
      .addCase(cancelReminder.rejected, (state, action) => {
        state.cancel = { id: null, error: action.payload ?? action.error.message ?? null };
      })
      // Gone from view at once; a failed dismissal only means it comes back
      // on the next load, which is better than a notice that won't close.
      .addCase(dismissMyReminder.pending, (state, action) => {
        if (state.myNotices.data) state.myNotices.data = state.myNotices.data.filter((r) => r.id !== action.meta.arg);
      });
  },
});

export const { clearSendStatus } = remindersSlice.actions;

/*
-------------------------------
SELECTORS (re-exported with RootState from _redux/index.ts)
-------------------------------
*/

export const getTemplates = (state: RemindersState) => state.templates;
export const getAudience = (state: RemindersState) => state.audience;
export const getHistory = (state: RemindersState) => state.history;
export const getSendStatus = (state: RemindersState) => state.send;
export const getCancelStatus = (state: RemindersState) => state.cancel;
/** The newest undismissed in-app reminder, or null. */
export const getLatestNotice = (state: RemindersState) => state.myNotices.data?.[0] ?? null;
export const getMyNoticesStatus = (state: RemindersState) => state.myNotices.status;

export default remindersSlice.reducer;
